import { FormMapping } from '@form/FormMapping';
import { Category } from 'components/ClubMemberManagement/types';
import { AutoCompleteTextInput } from 'components/Form/AutoCompleteTextInput';
import { Form } from 'components/Form/base/Form';
import FormListInput from 'components/Form/FormListInput';
import { FormDivider, FormInput } from 'components/Form/next';
import { SubmitInput } from 'components/Form/SubmitInput';
import Loader from 'components/Loader';
import { ExemptionsAssistent, exemptionsRules } from 'components/SpaceBooking/ExemptionsAssistent';
import Flex from 'components/styledComponents/Flex';
import { timeDivisions } from 'components/TrainingPlan/TrainingPlanHelpers';
import Joi from 'joi';
import { useState } from 'react';
import useSWR from 'swr';
import { useApi } from '../../../../hooks/useApi';
import { Space, SpaceSubscription } from '../../types';

interface Props {
    clubSlug: string;
    callback?: (s: SpaceSubscription) => void;
    subscription?: SpaceSubscription;
}

export const SpaceSubscriptionForm = ({ clubSlug, subscription, callback = () => {} }: Props) => {
    const api = useApi();
    const { data: formData, error: formError } = useSWR(`form/${subscription?.form_id}`);
    const { data: categoryData } = useSWR<{ data: Category[] }>(`club-member/${clubSlug}/category`);
    const { data: spacesData } = useSWR<{ data: Space[] }>(`space/${clubSlug}`);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = (data) => {
        return api({
            url: `space/subscription/${subscription?.id || clubSlug}`,
            method: subscription ? 'PUT' : 'POST',
            data: {
                ...data,
                form_mapping: { ...(data?.form_mapping || {}) },
                form_id: data.form?.id,
                guidelines: data.guidelines?.map((g) => g.value)
            },
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data }) => callback(data)
        });
    };

    const schema = Joi.object({
        title: Joi.string().required().max(255).label('Titel'),
        description: Joi.string().allow('', null).max(5000).label('Beschreibung'),
        start: Joi.date().required().label('Startdatum'),
        end: Joi.date()
            .required()
            .greater(Joi.ref('start', { render: true }))
            .label('Enddatum'),
        start_time: Joi.number().required().label('Startzeit'),
        end_time: Joi.number()
            .required()
            .greater(Joi.ref('start_time'))
            .label('Endzeit')
            .messages({ 'number.greater': 'Endzeit muss größer sein als Startzeit.' }),
        deadline: Joi.date().required().label('Anmeldeschluss'),
        repeat_period: Joi.string().required().valid('DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY').label('Buchungsrythmus'),
        price: Joi.number().min(0).required().label('Preis'),
        guidelines: Joi.array().allow(null).label('Buchungsrichtlinien'),
        allow_online_payments: Joi.boolean().allow(null).label('Online Zahlungen erlauben'),
        only_requests: Joi.boolean().allow(null).label('Online Zahlungen erlauben'),
        form: Joi.object({ id: Joi.number().min(0).required() })
            .required()
            .unknown()
            .label('Formular'),
        spaces: Joi.array()
            .allow(null)
            .items(Joi.alternatives(Joi.object({ id: Joi.number().required().min(0) }).unknown(), Joi.number().min(0)))
            .label('Plätze'),
        exemptions: exemptionsRules
    }).unknown();

    const possibleGuidelines = [
        { value: 'member', label: 'Mitglieder' },
        { value: 'relative', label: 'Verantwortliche' },
        ...(categoryData?.data || []).map((cat) => ({
            value: `category:${cat.id}`,
            label: `Mitglieder der Kategorie: ${cat.title}`
        }))
    ];

    return (
        <Loader isLoading={!formData && !formError}>
            <Form
                onSubmit={handleSubmit}
                validationSchema={schema}
                defaultValues={{
                    ...subscription,
                    form: formData?.data,
                    guidelines: possibleGuidelines.filter((g) => subscription?.guidelines?.includes(g.value))
                }}
            >
                <FormDivider title="Allgemein" />
                <FormInput name="title" label="Titel" />
                <FormInput.WYSIWYG name="description" label="Beschreibung" />
                <AutoCompleteTextInput
                    emptyLabel="Keine Plätze verfügbar."
                    data={spacesData?.data}
                    multiple
                    dataKey={(item: Space) => `${item.name} - ${item.space_group.name}`}
                    minimizedDataKey="id"
                    name="spaces"
                    label="Zugelassene Plätze"
                />
                <FormDivider title="Zeiten" />
                <Flex gap={0.5}>
                    <FormInput.DatePicker className="w-100" name="start" label="Startdatum" />
                    <FormInput.DatePicker className="w-100" name="end" label="Enddatum" />
                </Flex>
                <FormInput.DatePicker name="deadline" label="Anmeldeschluss" />
                <ExemptionsAssistent name="exemptions" />
                <Flex gap={0.5}>
                    <FormInput.Select
                        className="!tw-flex-1"
                        name="start_time"
                        label="Minimale Startzeit (meist Öffnungszeiten)"
                        options={timeDivisions.times.map((time, index) => ({ key: index, label: time }))}
                    />
                    <FormInput.Select
                        className="!tw-flex-1"
                        name="end_time"
                        label="Maximale Endzeit (meist Öffnungszeiten)"
                        options={timeDivisions.times.map((time, index) => ({ key: index, label: time }))}
                    />
                </Flex>
                <FormInput.Select
                    name="repeat_period"
                    label="Buchungsrythmus des Abos"
                    options={[
                        {
                            key: 'DAILY',
                            label: 'täglich'
                        },
                        {
                            key: 'WEEKLY',
                            label: 'wöchentlich'
                        },
                        {
                            key: 'MONTHLY',
                            label: 'monatlich'
                        },
                        {
                            key: 'YEARLY',
                            label: 'jährlich'
                        }
                    ]}
                />

                <FormInput
                    name="price"
                    label="Rückfall-Festpreis (falls keine Preisregeln gefunden)"
                    type="number"
                    step={0.01}
                    min={0}
                />
                <FormDivider title="Berechtigung" />
                <FormInput.Checkbox name="allow_online_payments" label="Online Zahlungen erlauben" />

                <FormInput.Checkbox name="only_requests" label="Buchungen nur auf Anfrage" />

                <AutoCompleteTextInput
                    name="guidelines"
                    label="Buchbar nur für"
                    multiple
                    dataKey="label"
                    data={possibleGuidelines}
                />
                <FormDivider title="Formular" />
                <FormListInput name="form" label="Formular auswählen" />
                <FormMapping />
                <SubmitInput block isLoading={isLoading}>
                    Speichern
                </SubmitInput>
            </Form>
        </Loader>
    );
};
